import {
  $framework,
  catchError,
  useSource,
  useTranslation,
} from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { useNavigate } from "@opendash/router";
import { Button, Flex, Modal, Popconfirm, Space, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { StepMedia, StepsTable } from "..";
import { Schedule_Execution } from "../../../js/parsegenerated";

type Props = {
  showChildren?: boolean;
};

export const ExecutionsTable = observer((props: Props) => {
  const { confirm } = Modal;
  const navigate = useNavigate();
  const t = useTranslation();
  const [source] = useSource();
  const [executions, setExecutions] = useState<Schedule_Execution[]>([]);

  async function removeScheduleExecution(execution: Schedule_Execution) {
    try {
      // const media =
      //   (await props.message.relation("media")?.query().find()) ?? [];
      // await Media.destroyAll(media);
      await execution.destroy();
    } catch (e) {
      console.error(e);
    }
  }

  const columns: ColumnsType<Schedule_Execution> = [
    {
      title: t("maintenance:schedule.source.columns.name.label"),
      dataIndex: "name",
      render: (_, record) => <span>{record.get("title")}</span>,
    },
    {
      title: t("maintenance:schedule.source.columns.description.label"),
      dataIndex: "description",
      render: (_, record) => <span>{record.get("description")}</span>,
    },
    // {
    //   title: t("maintenance:schedule.source.columns.finishedAt.label"),
    //   dataIndex: "finishedAt",
    //   render: (_, record) => (
    //     <span>
    //       {dayjs(record.get("finishedAt")).format("DD.MM.YYYY HH:mm")}
    //     </span>
    //   ),
    // },
    {
      title: "",
      dataIndex: "status",
      width: 200,
      render: (_, record) => {
        const tags = [];
        //Get the time of the execution
        tags.push(
          <Tag key={record.id + "createdAt"} color="default">
            {t("maintenance:schedule.execution.columns.createdAt.tag", {
              createdAt: dayjs(record.get("createdAt")).format(
                "DD.MM.YYYY HH:mm"
              ),
            })}
          </Tag>
        );

        if (record.get("finishedAt") !== undefined) {
          tags.push(
            <Tag key={record.id + "finished"} color="success">
              {t("maintenance:schedule.execution.columns.finishedAt.tag")}
            </Tag>
          );
        } else if (record.get("finishedAt") === undefined) {
          tags.push(
            <Tag key={record.id + "notfinished"} color="error">
              {t("maintenance:schedule.execution.columns.notfinished.tag")}
            </Tag>
          );
        }

        //Add a tag for the source
        if (record.get("source") !== undefined) {
          tags.push(
            <Tag key={record.id + "source"} color="default">
              {t("maintenance:schedule.execution.columns.action.source.label", {
                source: record.get("source").get("name"),
              })}
            </Tag>
          );
        }

        return (
          <Flex gap="middle" wrap="wrap">
            {tags}
          </Flex>
        );
      },
    },
    {
      title: t("maintenance:schedule.execution.columns.action.label"),
      dataIndex: "action",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          {record.get("finishedAt") === undefined ? (
            <Button
              shape="circle"
              onClick={() => {
                confirm({
                  title: t(
                    "maintenance:schedule.execution.columns.action.continue.confirm.title"
                  ),
                  icon: <Icon icon="fa:question"></Icon>,
                  content: t(
                    "maintenance:schedule.execution.columns.action.continue.confirm.content"
                  ),
                  okText: t("maintenance:global.button.ok_label"),
                  cancelText: t("maintenance:global.button.cancel_label"),
                  onOk() {
                    continueexecution(record);
                  },
                  onCancel() {},
                });
              }}
              icon={<Icon icon="fa:check"></Icon>}
            >
              {/* {t(
                "maintenance:schedule.execution.columns.action.continue.label"
              )} */}
            </Button>
          ) : null}
          <Button shape="circle" icon={<Icon icon="fa:eye" />}></Button>
          <Popconfirm
            title={t(
              "maintenance:schedule.execution.columns.action.delete.confirm.title"
            )}
            description={t(
              "maintenance:schedule.execution.columns.action.delete.confirm.description"
            )}
            onConfirm={() => removeScheduleExecution(record)}
            onCancel={() => {}}
            okText={t("maintenance:global.button.ok_label")}
            cancelText={t("maintenance:global.button.close_label")}
          >
            <Button shape="circle" icon={<Icon icon="fa:trash" />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (source === undefined) {
      return;
    }

    const sourceIds = [source.id];

    if (props.showChildren) {
      const subordinates =
        $framework.services.SourceService.getDescendents(source).map(
          (s) => s.id
        ) || [];
      sourceIds.push(...subordinates);
    }

    const fetch = async () => {
      //Create Queries for all sources
      const queries = sourceIds.map((id) => {
        return new Parse.Query(Schedule_Execution)
          .equalTo("source", {
            __type: "Pointer",
            className: Source.className,
            objectId: id,
          })
          .descending("createdAt")
          .includeAll();
      });

      const [error, executions] = await catchError(
        Parse.Query.or(...queries).find()
      );

      setExecutions(executions);
    };

    fetch();
  }, [source.id]);

  const continueexecution = async (record: Schedule_Execution) => {
    navigate(`/openservice/schedule/${record.id}/execute`);
  };

  return (
    <>
      {/* <Typography.Title level={3}>
        {t("maintenance:schedule.source.executions.headline")}
      </Typography.Title> */}
      <Table
        columns={columns}
        dataSource={executions}
        size="small"
        rowKey={(record: Schedule_Execution) => record.id}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <>
                {record.get("origin") !== undefined ? (
                  <StepMedia scheduletemplate={record.get("origin")} />
                ) : null}
                <StepsTable schedule={record} />
              </>
            );
          },
          rowExpandable: (record) => {
            return true;
          },
        }}
      />
    </>
  );
});
